import Grid from '@mui/material/Grid';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Search from '@mui/icons-material/Search';
import Clear from '@mui/icons-material/Clear';
import { Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { isEditorActive } from '@sitecore-jss/sitecore-jss-nextjs/utils';
import { getConditionSearchBox } from 'lib/utils/coveo/searchBoxes';
import { ChangeEvent, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import useDidYouMean from 'lib/hooks/coveo/useDidYouMean';
import UiFormLabel from '../shared/FormLabel/FormLabel';
import { setConditionFilterValue } from 'src/features/coveoSlice';
import { useDispatch } from 'react-redux';

const ConditionFilter = (props: any): JSX.Element => {
  const isRequired = props?.isRequired;
  const requiredMessage = props?.requiredMessage;
  const [isConditionFieldEmpty, setIsConditionFieldEmpty] = useState(false);
  const { sitecoreContext } = useSitecoreContext();
  const conditionSearchBox = useMemo(
    () => getConditionSearchBox(props?.fields?.totalSuggestion?.value),
    []
  );
  const dispatch = useDispatch();
  useEffect(() => {
    if (conditionSearchBox?.state?.value === '' && isRequired) {
      setIsConditionFieldEmpty(true);
    } else {
      setIsConditionFieldEmpty(false);
    }
  }, [conditionSearchBox?.state?.value, isRequired]);

  const [state, setState] = useState<SearchBoxState>(conditionSearchBox.state);
  const LENGTH_TO_SUGGESTIONS = props?.lengthToSuggest || 3;
  const { hasQueryCorrection, queryCorrection } = useDidYouMean();
  const suggestOptions =
    state.value.length >= LENGTH_TO_SUGGESTIONS ? state.suggestions.map((s) => s.rawValue) : [];

  useEffect(() => {
    const corrections =
      hasQueryCorrection && queryCorrection.wordCorrections
        ? queryCorrection?.wordCorrections[0]
        : undefined;
    if (corrections?.correctedWord) {
      conditionSearchBox.updateText(corrections.correctedWord);
    }
  }, [hasQueryCorrection, queryCorrection?.wordCorrections]);

  const onChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    conditionSearchBox.updateText(e.target.value);
    conditionSearchBox.subscribe(() => {
      setState(conditionSearchBox.state);
    });
  };

  const handleSearch = () => {
    conditionSearchBox.submit();
  };
  const handleReset = () => {
    conditionSearchBox.clear();
    handleSearch();
  };

  useEffect(() => conditionSearchBox.subscribe(() => setState(conditionSearchBox.state)), []);

  const onInputChange = (_: SyntheticEvent<Element, Event>, value: string) => {
    if (value.length > props?.coiInputTextLimit) {
      return;
    }

    dispatch(setConditionFilterValue(value));
    conditionSearchBox.updateText(value);
    conditionSearchBox.subscribe(() => {
      setState(conditionSearchBox.state);
    });
  };

  useEffect(() => {
    if (isEditorActive() || sitecoreContext?.pageState === 'preview') return;
    conditionSearchBox.showSuggestions();
    const subscribe = conditionSearchBox.subscribe(() => {
      setState(conditionSearchBox.state);
    });

    return () => subscribe();
  }, []);

  const { fields } = props?.fields?.data[0];
  return (
    <Grid item>
      <UiFormLabel>
        <Text field={fields.label}></Text>
      </UiFormLabel>
      <Autocomplete
        freeSolo
        disableClearable
        id={'search-box'}
        value={state.value}
        onInputChange={onInputChange}
        onChange={onChange}
        options={suggestOptions}
        sx={{
          width: '100%',
          '& input': { color: (theme) => `${theme.palette.neutral7.main} !important` },
        }}
        size="small"
        renderInput={(params) => (
          <TextField
            {...params}
            inputProps={{
              ...params?.inputProps,
              value: state.value,
              'aria-label': 'Condition filter Search',
            }}
            label={''}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {state.value.length > 0 && (
                    <Clear style={{ cursor: 'pointer' }} onClick={handleReset} />
                  )}
                  <Search
                    style={{ cursor: 'pointer', marginLeft: '0.25rem' }}
                    onClick={handleSearch}
                  />
                </>
              ),
            }}
          />
        )}
      />
      {isConditionFieldEmpty && <Typography sx={{ color: 'red' }}>{requiredMessage}</Typography>}
    </Grid>
  );
};

export default ConditionFilter;
